/**
 * FutureFaresPage · v2.7.65+E215
 *
 * Admin dispatch view for advance bookings (>= 24h out) stored in the isolated
 * `future_fares` collection. Layout: a month calendar grid (click a day to open
 * that day's dispatch sheet) + a "Ready for Dispatch" queue for fares
 * approaching within 1 hour. Dispatchers can assign / reassign a driver inline
 * using the SAME active-driver list the Book Now form uses
 * (/admin/queued-trips/active-drivers).
 *
 * Read/writes ONLY the future_fares endpoints — nothing else in the console is
 * touched.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarClock, ChevronLeft, ChevronRight, ArrowLeft, Loader2, RefreshCw,
  MapPin, User, DollarSign, CreditCard, StickyNote, Car, Clock, AlertTriangle, Send,
  Flag, Inbox, CheckCircle2, Ban, Timer, Eye, EyeOff, Archive, ArchiveRestore, Search,
} from "lucide-react";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CustomerTypeBadge } from "@/components/CustomerTypeBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const CT_TZ = "America/Chicago";

function localDateKey(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  // Group by the CENTRAL-time calendar day (Buller's operating timezone),
  // not the viewer's browser timezone.
  const p = new Intl.DateTimeFormat("en-CA", {
    timeZone: CT_TZ, year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(d);
  const get = (t) => (p.find((x) => x.type === t) || {}).value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString(undefined, { timeZone: CT_TZ, hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}
function fmtDateLong(key) {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}
function money(n) {
  const v = Number(n);
  return isNaN(v) ? "—" : `$${v.toFixed(2)}`;
}
function isApproaching(f) {
  if (f.ready_for_dispatch || f.status === "ready_for_dispatch") return true;
  const ms = new Date(f.requested_pickup_at).getTime() - Date.now();
  return ms <= 60 * 60 * 1000 && ms > -6 * 60 * 60 * 1000;
}

// v2.7.65+E218 · A ride that still has NO assigned driver as pickup nears
// (within 3h, or already overdue but not ancient) is a red-flag risk.
const MISSING_DRIVER_WINDOW_MS = 3 * 60 * 60 * 1000;
function isMissingDriver(f) {
  if (f.status === "cancelled" || f.status === "completed") return false;
  if (f.assigned_driver_id) return false;
  const ms = new Date(f.requested_pickup_at).getTime() - Date.now();
  return ms <= MISSING_DRIVER_WINDOW_MS && ms > -12 * 60 * 60 * 1000;
}

function StatusBadge({ f }) {
  if (f.status === "cancelled")
    return <Badge variant="outline" className="border-zinc-600 text-zinc-400 text-[10px] uppercase">Cancelled</Badge>;
  if (f.status === "completed")
    return <Badge variant="outline" className="border-emerald-400/50 text-emerald-300 text-[10px] uppercase">✓ Completed</Badge>;
  if (f.status === "in_progress")
    return <Badge variant="outline" className="border-sky-400/60 text-sky-300 text-[10px] uppercase">Driver started</Badge>;
  if (f.status === "dispatched")
    return <Badge variant="outline" className="border-sky-400/50 text-sky-300 text-[10px] uppercase">Dispatched</Badge>;
  if (isApproaching(f))
    return <Badge variant="outline" className="border-amber-400/60 text-amber-300 text-[10px] uppercase">⚠ Approaching · within 1h</Badge>;
  return <Badge variant="outline" className="border-violet-400/50 text-violet-300 text-[10px] uppercase">Scheduled</Badge>;
}

function FareCard({ f, drivers, onAssign, assigning, onRelease, releasing, onCancel, cancelling, onCloseOut, closingOut, onAutoRelease, autoReleasing, archived = false, onArchive, onUnarchive, archiving }) {
  const missing = isMissingDriver(f);
  return (
    <Card
      data-testid={`ff-card-${f.id}`}
      className={`bg-zinc-900 p-4 space-y-3 ${
        archived
          ? "border-zinc-700"
          : missing
            ? "border-rose-500/60"
            : isApproaching(f) && f.status !== "cancelled"
              ? "border-amber-400/50"
              : "border-zinc-800"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="text-amber-300 font-mono text-[11px]">{f.id}</div>
          <StatusBadge f={f} />
          {archived && (
            <Badge variant="outline" className="border-zinc-500 text-zinc-300 text-[10px] uppercase" data-testid={`ff-archived-badge-${f.id}`}>
              <Archive className="h-3 w-3 mr-1" />Archived
            </Badge>
          )}
          {missing && !archived && (
            <Badge variant="outline" className="border-rose-500/60 text-rose-300 text-[10px] uppercase animate-pulse" data-testid={`ff-missing-driver-${f.id}`}>
              <Flag className="h-3 w-3 mr-1" />No Driver
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 text-amber-200 font-bold text-sm whitespace-nowrap">
          <Clock className="h-3.5 w-3.5" />{fmtTime(f.requested_pickup_at)}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <div className="flex items-center gap-1.5 text-zinc-200" data-testid={`ff-card-${f.id}-customer`}>
          <User className="h-3.5 w-3.5 text-zinc-500" /><span className="font-bold">{f.customer_name || "—"}</span>
          {f.rider?.phone && <span className="text-zinc-400">· {f.rider.phone}</span>}
          {f.customer_type && <CustomerTypeBadge type={f.customer_type} short className="ml-1" testid={`ff-card-${f.id}-customer-type`}/>}
          {f.group_rider_count ? <span className="ml-1 text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 border border-violet-500/40" data-testid={`ff-card-${f.id}-group-riders`}>Group · {f.group_rider_count} riders</span> : null}
        </div>
        <div className="flex items-center gap-1.5 text-zinc-300">
          <DollarSign className="h-3.5 w-3.5 text-zinc-500" />{money(f.fare_total)}
          <CreditCard className="h-3.5 w-3.5 text-zinc-500 ml-2" /><span className="uppercase">{f.payment_method}</span>
        </div>
        <div className="flex items-start gap-1.5 text-zinc-300 sm:col-span-2">
          <MapPin className="h-3.5 w-3.5 text-emerald-500 mt-0.5" />
          <span>{f.pickup_address} <span className="text-zinc-500">→</span> {f.dropoff_address}</span>
        </div>
        <div className="text-zinc-400 text-[11px]">Booked by <span className="text-zinc-200">{f.booked_by_agent || "—"}</span></div>
        <div className="text-zinc-400 text-[11px]">Passengers <span className="text-zinc-200">{f.passenger_count}</span></div>
        {f.dispatcher_notes && (
          <div className="flex items-start gap-1.5 text-zinc-300 sm:col-span-2">
            <StickyNote className="h-3.5 w-3.5 text-amber-500 mt-0.5" /><span className="italic">{f.dispatcher_notes}</span>
          </div>
        )}
      </div>

      {/* Inline driver assign / reassign (same active-driver list as Book Now) */}
      {!archived ? (
      <div className="flex items-center gap-2 pt-1 border-t border-zinc-800">
        <Car className="h-4 w-4 text-zinc-500" />
        <span className="text-[11px] uppercase tracking-wider text-zinc-500">Driver</span>
        <Select
          value={f.assigned_driver_id || "__none__"}
          onValueChange={(v) => onAssign(f, v)}
          disabled={f.status === "cancelled" || assigning === f.id}
        >
          <SelectTrigger data-testid={`ff-assign-${f.id}`} className="h-8 text-xs bg-zinc-950 border-zinc-700 max-w-[280px]">
            <SelectValue placeholder="Assign a driver…" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
            <SelectItem value="__none__">Unassigned</SelectItem>
            {drivers.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}{d.on_trip ? " · on trip" : d.online ? " · available" : " · offline"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {assigning === f.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
        {f.assigned_driver_name && assigning !== f.id && (
          <span className="text-[11px] text-emerald-300" data-testid={`ff-driver-${f.id}`}>✓ {f.assigned_driver_name}</span>
        )}
      </div>
      ) : (
        <div className="flex items-center gap-2 pt-1 border-t border-zinc-800 text-xs">
          <Car className="h-4 w-4 text-zinc-500" />
          <span className="text-[11px] uppercase tracking-wider text-zinc-500">Driver</span>
          <span className="text-zinc-200" data-testid={`ff-driver-${f.id}`}>{f.assigned_driver_name || "—"}</span>
        </div>
      )}

      {/* Release-to-driver control (E216). A driver cannot start until released. */}
      {f.status !== "cancelled" && f.status !== "completed" && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-zinc-800">
          {f.released ? (
            <Badge variant="outline" className="border-emerald-400/50 text-emerald-300 text-[10px] uppercase" data-testid={`ff-released-${f.id}`}>
              ✓ Released to driver
            </Badge>
          ) : (
            <>
              <Button
                type="button"
                size="sm"
                onClick={() => onRelease(f)}
                disabled={!f.assigned_driver_id || releasing === f.id}
                data-testid={`ff-release-${f.id}`}
                className="h-8 text-xs bg-violet-500 hover:bg-violet-400 text-white font-bold disabled:opacity-40"
              >
                {releasing === f.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                Release to Driver
              </Button>
              {!f.assigned_driver_id && <span className="text-[10px] text-zinc-500">Assign a driver first to enable release.</span>}
            </>
          )}
          {f.status === "in_progress" && (
            <span className="text-[10px] text-sky-300">Driver is on this trip.</span>
          )}
        </div>
      )}

      {/* v2.7.65+E235 · Auto-Release — release to the assigned driver N min before pickup */}
      {f.status !== "cancelled" && f.status !== "completed" && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-zinc-800">
          <label className="flex items-center gap-1.5 cursor-pointer text-[11px] text-zinc-300" data-testid={`ff-autorelease-toggle-${f.id}`}>
            <input
              type="checkbox"
              checked={!!f.auto_release}
              disabled={autoReleasing === f.id}
              onChange={(e) => onAutoRelease(f, e.target.checked, f.auto_release_minutes || 30)}
              className="h-3.5 w-3.5 rounded accent-violet-400"
            />
            <Timer className="h-3.5 w-3.5 text-violet-300" />Auto-release
          </label>
          <Select
            value={String(f.auto_release_minutes || 30)}
            onValueChange={(v) => onAutoRelease(f, !!f.auto_release, parseInt(v, 10))}
            disabled={autoReleasing === f.id}
          >
            <SelectTrigger data-testid={`ff-autorelease-mins-${f.id}`} className="h-7 text-[11px] bg-zinc-950 border-zinc-700 w-[130px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-700 text-zinc-100">
              {[15, 30, 60, 120].map((m) => <SelectItem key={m} value={String(m)}>{m} min before</SelectItem>)}
            </SelectContent>
          </Select>
          {f.auto_release && (
            <Badge variant="outline" className="border-violet-400/50 text-violet-300 text-[10px] uppercase" data-testid={`ff-autorelease-on-${f.id}`}>
              Auto · {f.auto_release_minutes || 30}m before
            </Badge>
          )}
          {f.auto_release && !f.assigned_driver_id && <span className="text-[10px] text-zinc-500">Assign a driver so it can fire.</span>}
          {autoReleasing === f.id && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-400" />}
        </div>
      )}

      {/* v2.7.65+E235 · Dispatch close-out / cancel */}
      {f.status !== "cancelled" && f.status !== "completed" && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-zinc-800">
          <Button
            type="button" size="sm" onClick={() => onCloseOut(f)} disabled={closingOut === f.id}
            data-testid={`ff-closeout-${f.id}`}
            className="h-8 text-xs bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold disabled:opacity-40"
          >
            {closingOut === f.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
            Close Out
          </Button>
          <Button
            type="button" size="sm" variant="outline" onClick={() => onCancel(f)} disabled={cancelling === f.id}
            data-testid={`ff-cancel-${f.id}`}
            className="h-8 text-xs bg-zinc-950 border-rose-500/50 text-rose-300 hover:bg-rose-500/10 disabled:opacity-40"
          >
            {cancelling === f.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Ban className="h-3.5 w-3.5 mr-1.5" />}
            Cancel
          </Button>
          <span className="text-[10px] text-zinc-500">Close Out records a completed trip (Completed Rides + driver history).</span>
        </div>
      )}
      {f.status === "cancelled" && f.cancel_reason && (
        <div className="text-[10px] text-zinc-500 pt-1 border-t border-zinc-800" data-testid={`ff-cancel-reason-${f.id}`}>Cancelled — {f.cancel_reason}</div>
      )}

      {/* v2.8+E298 · Archive (completed, active view) / Unarchive (archived view) */}
      {!archived && f.status === "completed" && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-zinc-800">
          <Button
            type="button" size="sm" onClick={() => onArchive(f)} disabled={archiving === f.id}
            data-testid={`ff-archive-${f.id}`}
            className="h-8 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-600 text-zinc-200 font-bold disabled:opacity-40"
          >
            {archiving === f.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Archive className="h-3.5 w-3.5 mr-1.5" />}
            Archive
          </Button>
          <span className="text-[10px] text-zinc-500">Move this completed fare to the Archived view — it stays viewable and is never deleted.</span>
        </div>
      )}
      {archived && (
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-zinc-800">
          <Button
            type="button" size="sm" variant="outline" onClick={() => onUnarchive(f)} disabled={archiving === f.id}
            data-testid={`ff-unarchive-${f.id}`}
            className="h-8 text-xs bg-zinc-950 border-violet-500/50 text-violet-300 hover:bg-violet-500/10 disabled:opacity-40"
          >
            {archiving === f.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ArchiveRestore className="h-3.5 w-3.5 mr-1.5" />}
            Restore to Active
          </Button>
          {f.archived_at && (
            <span className="text-[10px] text-zinc-500" data-testid={`ff-archived-at-${f.id}`}>
              Archived {new Date(f.archived_at).toLocaleString(undefined, { timeZone: CT_TZ })}
            </span>
          )}
        </div>
      )}
    </Card>
  );
}

function ImportScheduledDialog({ open, onOpenChange, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runPreview = useCallback(async () => {
    setLoading(true);
    setPreview(null);
    try {
      const { data } = await adminApi.post("/future-fares/import-scheduled", { apply: false });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not scan the dispatch queue.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-run the dry-run preview each time the dialog opens.
  useEffect(() => {
    if (open) { setPreview(null); runPreview(); }
  }, [open, runPreview]);

  const apply = useCallback(async () => {
    setApplying(true);
    try {
      const { data } = await adminApi.post("/future-fares/import-scheduled", { apply: true });
      toast.success(`Imported ${data.matched} scheduled ride${data.matched === 1 ? "" : "s"} into Future Fares.`);
      onOpenChange(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed.");
    } finally {
      setApplying(false);
    }
  }, [onOpenChange, onDone]);

  const rows = (preview && preview.migrated) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-zinc-100 max-w-2xl" data-testid="ff-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-amber-300" />Import Scheduled Rides into Future Fares
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Pulls still-pending, <span className="text-zinc-200 font-semibold">unassigned</span> scheduled rides whose pickup is
            <span className="text-zinc-200 font-semibold"> 24h+ away</span> out of the live dispatch queue and into Future Fares
            (locked until you release them). Assigned, dispatched, and completed rides are never touched.
          </DialogDescription>
        </DialogHeader>

        <div className="py-1 max-h-[50vh] overflow-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-zinc-400 py-6 justify-center" data-testid="ff-import-loading">
              <Loader2 className="h-4 w-4 animate-spin" />Scanning the dispatch queue…
            </div>
          ) : !preview ? (
            <div className="text-sm text-zinc-500 py-6 text-center">No preview yet.</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-zinc-400 py-6 text-center" data-testid="ff-import-empty">
              <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-emerald-400" />
              No mis-routed scheduled rides found. Nothing to import — the queue is clean.
            </div>
          ) : (
            <>
              <div className="text-xs text-zinc-400 mb-2" data-testid="ff-import-count">
                Found <span className="text-amber-300 font-bold">{rows.length}</span> ride{rows.length === 1 ? "" : "s"} to move
                {preview.scanned != null && <span className="text-zinc-600"> · scanned {preview.scanned}</span>}
              </div>
              <div className="space-y-2">
                {rows.map((r) => (
                  <div key={r.trip_id} className="rounded-md border border-zinc-800 bg-zinc-950 p-2.5 text-xs" data-testid={`ff-import-row-${r.trip_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[10px] text-zinc-500">{r.trip_id}</span>
                      <span className="flex items-center gap-1 text-amber-200 font-bold"><Clock className="h-3 w-3" />{fmtTime(r.requested_pickup_at)} · {localDateKey(r.requested_pickup_at)}</span>
                    </div>
                    <div className="text-zinc-200 font-semibold mt-1">{r.customer_name}</div>
                    <div className="text-zinc-400 flex items-start gap-1 mt-0.5">
                      <MapPin className="h-3 w-3 text-emerald-500 mt-0.5" />
                      <span>{r.pickup_address} <span className="text-zinc-600">→</span> {r.dropoff_address}</span>
                    </div>
                    <div className="text-zinc-500 mt-0.5">{money(r.fare_total)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}
            data-testid="ff-import-cancel" className="bg-zinc-950 border-zinc-700 text-xs">
            Cancel
          </Button>
          <Button variant="outline" onClick={runPreview} disabled={loading || applying}
            data-testid="ff-import-rescan" className="bg-zinc-950 border-zinc-700 text-xs">
            <RefreshCw className="h-3.5 w-3.5 mr-1" />Re-scan
          </Button>
          <Button onClick={apply} disabled={loading || applying || rows.length === 0}
            data-testid="ff-import-apply" className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-bold text-xs disabled:opacity-40">
            {applying ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Inbox className="h-3.5 w-3.5 mr-1" />}
            Import {rows.length > 0 ? rows.length : ""} to Future Fares
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FutureFaresPage() {
  const navigate = useNavigate();
  const [fares, setFares] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [releasing, setReleasing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [closingOut, setClosingOut] = useState(null);
  const [autoReleasing, setAutoReleasing] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const [view, setView] = useState("active");   // "active" | "archived"
  const [archivedFares, setArchivedFares] = useState([]);
  const [archivedSearch, setArchivedSearch] = useState("");
  const [archivedDate, setArchivedDate] = useState("");
  const [viewMonth, setViewMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [selectedDay, setSelectedDay] = useState(null);   // "YYYY-MM-DD"
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ffRes, arcRes, drvRes] = await Promise.all([
        adminApi.get("/future-fares", { params: { limit: 1000 } }),
        adminApi.get("/future-fares", { params: { limit: 1000, archived: true } }),
        adminApi.get("/admin/queued-trips/active-drivers").catch(() => ({ data: { drivers: [] } })),
      ]);
      setFares((ffRes.data && ffRes.data.future_fares) || []);
      setArchivedFares((arcRes.data && arcRes.data.future_fares) || []);
      setDrivers((drvRes.data && drvRes.data.drivers) || []);
    } catch (e) {
      toast.error("Could not load Future Fares.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const assignDriver = useCallback(async (fare, driverId) => {
    setAssigning(fare.id);
    try {
      if (driverId === "__none__") {
        await adminApi.post(`/future-fares/${fare.id}/assign-driver`, { assigned_driver_id: "", assigned_driver_name: null });
        toast.success(`Driver cleared on ${fare.id}`);
      } else {
        const drv = drivers.find((d) => d.id === driverId);
        await adminApi.post(`/future-fares/${fare.id}/assign-driver`, {
          assigned_driver_id: driverId, assigned_driver_name: drv?.name || null,
        });
        toast.success(`Assigned ${drv?.name || "driver"} to ${fare.id}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign driver.");
    } finally {
      setAssigning(null);
    }
  }, [drivers, load]);

  const releaseFare = useCallback(async (fare) => {
    setReleasing(fare.id);
    try {
      await adminApi.post(`/future-fares/${fare.id}/release`);
      toast.success(`Released ${fare.id} to ${fare.assigned_driver_name || "driver"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not release this fare.");
    } finally {
      setReleasing(null);
    }
  }, [load]);

  // v2.7.65+E235 · Cancel a future fare (optional reason).
  const cancelFare = useCallback(async (fare) => {
    const reason = window.prompt(`Cancel future fare ${fare.id}?\n\nOptional reason (leave blank to skip):`, "");
    if (reason === null) return; // dispatcher aborted
    setCancelling(fare.id);
    try {
      await adminApi.post(`/future-fares/${fare.id}/cancel`, { reason: reason || null });
      toast.success(`Cancelled ${fare.id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel this fare.");
    } finally {
      setCancelling(null);
    }
  }, [load]);

  // v2.7.65+E235 · Close out (complete) a future fare directly from dispatch.
  const closeOutFare = useCallback(async (fare) => {
    if (!window.confirm(`Close out ${fare.id} as COMPLETED?\n\nThis records a completed trip in Completed Rides + driver history and cannot be undone.`)) return;
    setClosingOut(fare.id);
    try {
      const { data } = await adminApi.post(`/future-fares/${fare.id}/close-out`);
      toast.success(data?.already_completed ? `${fare.id} was already completed.` : `Closed out ${fare.id} · trip recorded.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not close out this fare.");
    } finally {
      setClosingOut(null);
    }
  }, [load]);

  // v2.7.65+E235 · Toggle auto-release + set the lead time.
  const setAutoRelease = useCallback(async (fare, enabled, minutes) => {
    setAutoReleasing(fare.id);
    try {
      await adminApi.post(`/future-fares/${fare.id}/auto-release`, { enabled, minutes_before: minutes });
      toast.success(enabled ? `Auto-release ON · ${minutes}m before pickup` : `Auto-release off for ${fare.id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update auto-release.");
    } finally {
      setAutoReleasing(null);
    }
  }, [load]);

  // v2.8+E298 · Archive a COMPLETED fare (moves it to the Archived view).
  const archiveFare = useCallback(async (fare) => {
    if (!window.confirm(`Archive ${fare.id}?\n\nIt moves to the Archived view — still fully viewable for reference, and never deleted.`)) return;
    setArchiving(fare.id);
    try {
      await adminApi.post(`/future-fares/${fare.id}/archive`);
      toast.success(`Archived ${fare.id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not archive this fare.");
    } finally {
      setArchiving(null);
    }
  }, [load]);

  // v2.8+E298 · Restore an archived fare back to the active list.
  const unarchiveFare = useCallback(async (fare) => {
    setArchiving(fare.id);
    try {
      await adminApi.post(`/future-fares/${fare.id}/unarchive`);
      toast.success(`Restored ${fare.id} to Active`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore this fare.");
    } finally {
      setArchiving(null);
    }
  }, [load]);

  // ── Derived data ──
  const activeFares = useMemo(() => fares.filter((f) => f.status !== "cancelled"), [fares]);

  // v2.7.65+E235 · When "Show cancelled" is on, include cancelled fares in the
  // calendar + day sheet so dispatch can audit them.
  // v2.8+E298 · The Archived view swaps the whole source to archived fares.
  const displayFares = useMemo(
    () => (view === "archived" ? archivedFares : (showCancelled ? fares : activeFares)),
    [view, archivedFares, showCancelled, fares, activeFares]
  );
  const isArchivedView = view === "archived";

  const readyQueue = useMemo(
    () => activeFares.filter((f) => isApproaching(f) && f.status !== "dispatched")
      .sort((a, b) => new Date(a.requested_pickup_at) - new Date(b.requested_pickup_at)),
    [activeFares]
  );

  // v2.7.65+E218 · Upcoming fares still without a driver as pickup nears.
  const missingDriverFares = useMemo(
    () => activeFares.filter(isMissingDriver)
      .sort((a, b) => new Date(a.requested_pickup_at) - new Date(b.requested_pickup_at)),
    [activeFares]
  );

  const byDay = useMemo(() => {
    const map = {};
    for (const f of displayFares) {
      const k = localDateKey(f.requested_pickup_at);
      (map[k] = map[k] || []).push(f);
    }
    for (const k of Object.keys(map)) map[k].sort((a, b) => new Date(a.requested_pickup_at) - new Date(b.requested_pickup_at));
    return map;
  }, [displayFares]);

  const filteredSelectedDay = useMemo(() => {
    if (!selectedDay) return [];
    let rows = byDay[selectedDay] || [];
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((f) =>
        (f.customer_name || "").toLowerCase().includes(q) ||
        (f.pickup_address || "").toLowerCase().includes(q) ||
        (f.dropoff_address || "").toLowerCase().includes(q) ||
        (f.id || "").toLowerCase().includes(q) ||
        (f.booked_by_agent || "").toLowerCase().includes(q));
    }
    return rows;
  }, [selectedDay, byDay, search]);

  // v2.8+E299 · Archived-view name/date search (client-side over all archived).
  const filteredArchived = useMemo(() => {
    let rows = [...archivedFares];
    const q = archivedSearch.trim().toLowerCase();
    if (q) {
      rows = rows.filter((f) =>
        (f.customer_name || "").toLowerCase().includes(q) ||
        (f.rider?.phone || "").toLowerCase().includes(q) ||
        (f.pickup_address || "").toLowerCase().includes(q) ||
        (f.dropoff_address || "").toLowerCase().includes(q) ||
        (f.id || "").toLowerCase().includes(q) ||
        (f.booked_by_agent || "").toLowerCase().includes(q));
    }
    if (archivedDate) {
      rows = rows.filter((f) => localDateKey(f.requested_pickup_at) === archivedDate);
    }
    rows.sort((a, b) => new Date(b.requested_pickup_at) - new Date(a.requested_pickup_at));
    return rows;
  }, [archivedFares, archivedSearch, archivedDate]);

  // ── Calendar grid ──
  const calendarCells = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const first = new Date(year, month, 1);
    const startOffset = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startOffset; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const key = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      cells.push({ day: d, key, fares: byDay[key] || [] });
    }
    return cells;
  }, [viewMonth, byDay]);

  const todayKey = localDateKey(new Date().toISOString());

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-4 md:p-6" data-testid="future-fares-page">
      <div className="max-w-6xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 pb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-violet-500/10 border border-violet-500/40 flex items-center justify-center text-violet-400">
              <CalendarClock className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Future Fares</h1>
              <div className="text-[11px] text-zinc-400 uppercase tracking-widest font-bold">
                Advance bookings · 24h+ ahead · dispatch calendar
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* v2.8+E298 · Active / Archived view toggle */}
            <div className="inline-flex rounded-md border border-zinc-700 overflow-hidden" data-testid="ff-view-toggle">
              <button
                onClick={() => setView("active")}
                data-testid="ff-view-active"
                className={`h-9 px-3 text-xs font-bold uppercase tracking-wider transition-colors ${view === "active" ? "bg-violet-500/20 text-violet-200" : "bg-zinc-950 text-zinc-400 hover:text-zinc-200"}`}
              >
                Active <span className="font-mono">{activeFares.length}</span>
              </button>
              <button
                onClick={() => { setView("archived"); setSelectedDay(null); }}
                data-testid="ff-view-archived"
                className={`h-9 px-3 text-xs font-bold uppercase tracking-wider transition-colors border-l border-zinc-700 ${view === "archived" ? "bg-zinc-700/60 text-zinc-100" : "bg-zinc-950 text-zinc-400 hover:text-zinc-200"}`}
              >
                <Archive className="h-3.5 w-3.5 mr-1 inline" />Archived <span className="font-mono">{archivedFares.length}</span>
              </button>
            </div>
            {!isArchivedView && (
              <Button variant="outline" size="sm" onClick={() => setShowCancelled((v) => !v)} disabled={loading}
                data-testid="ff-toggle-cancelled" className={`bg-zinc-950 h-9 text-xs ${showCancelled ? "border-zinc-500 text-zinc-200" : "border-zinc-700 text-zinc-400"}`}>
                {showCancelled ? <EyeOff className="h-3.5 w-3.5 mr-1" /> : <Eye className="h-3.5 w-3.5 mr-1" />}
                {showCancelled ? "Hide Cancelled" : "Show Cancelled"}
              </Button>
            )}
            {!isArchivedView && (
              <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} disabled={loading}
                data-testid="ff-import-open" className="bg-zinc-950 border-amber-500/50 text-amber-300 hover:bg-amber-500/10 h-9 text-xs">
                <Inbox className="h-3.5 w-3.5 mr-1" />Import Scheduled Rides
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={load} disabled={loading}
              data-testid="ff-refresh" className="bg-zinc-950 border-zinc-700 h-9 text-xs">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate("/admin")}
              data-testid="ff-back" className="bg-zinc-950 border-zinc-700 h-9 text-xs">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />Back to Console
            </Button>
          </div>
        </div>

        {/* Missing-driver alert — upcoming fares still without a driver */}
        {!isArchivedView && missingDriverFares.length > 0 && (
          <Card data-testid="ff-missing-driver-alert" className="bg-rose-500/5 border-rose-500/50 p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="relative flex h-4 w-4 items-center justify-center">
                <span className="absolute inline-flex h-full w-full rounded-full bg-rose-500/40 animate-ping" />
                <Flag className="relative h-4 w-4 text-rose-400" />
              </span>
              <h2 className="text-sm font-black uppercase tracking-wider text-rose-300">
                Needs a Driver · {missingDriverFares.length} upcoming ride{missingDriverFares.length === 1 ? "" : "s"} still unassigned as pickup nears
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {missingDriverFares.map((f) => (
                <FareCard key={f.id} f={f} drivers={drivers} onAssign={assignDriver} assigning={assigning} onRelease={releaseFare} releasing={releasing} onCancel={cancelFare} cancelling={cancelling} onCloseOut={closeOutFare} closingOut={closingOut} onAutoRelease={setAutoRelease} autoReleasing={autoReleasing} onArchive={archiveFare} onUnarchive={unarchiveFare} archiving={archiving} />
              ))}
            </div>
          </Card>
        )}

        {/* Ready-for-dispatch queue */}
        {!isArchivedView && (
        <Card data-testid="ff-ready-queue" className="bg-amber-400/5 border-amber-400/40 p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="relative flex h-4 w-4 items-center justify-center">
              <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400/40 animate-ping" />
              <AlertTriangle className="relative h-4 w-4 text-amber-300" />
            </span>
            <h2 className="text-sm font-black uppercase tracking-wider text-amber-300">
              Ready for Dispatch · approaching within 1 hour ({readyQueue.length})
            </h2>
          </div>
          {readyQueue.length === 0 ? (
            <div className="text-xs text-zinc-500 italic">No future fares are within an hour of pickup right now.</div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {readyQueue.map((f) => (
                <FareCard key={f.id} f={f} drivers={drivers} onAssign={assignDriver} assigning={assigning} onRelease={releaseFare} releasing={releasing} onCancel={cancelFare} cancelling={cancelling} onCloseOut={closeOutFare} closingOut={closingOut} onAutoRelease={setAutoRelease} autoReleasing={autoReleasing} onArchive={archiveFare} onUnarchive={unarchiveFare} archiving={archiving} />
              ))}
            </div>
          )}
        </Card>
        )}

        {/* v2.8+E298 · Archived read-only list — full booking detail for reference */}
        {isArchivedView && (
          <Card data-testid="ff-archived-list" className="bg-zinc-900 border-zinc-800 p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2">
                <Archive className="h-4 w-4 text-zinc-400" />
                <h2 className="text-sm font-black uppercase tracking-wider text-zinc-300">
                  Archived Fares · read-only ({filteredArchived.length}{filteredArchived.length !== archivedFares.length ? ` of ${archivedFares.length}` : ""})
                </h2>
              </div>
              {/* v2.8+E299 · Name / date search */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 text-zinc-500 absolute left-2 top-1/2 -translate-y-1/2" />
                  <Input
                    value={archivedSearch}
                    onChange={(e) => setArchivedSearch(e.target.value)}
                    placeholder="Search name, phone, address, ID…"
                    data-testid="ff-archived-search"
                    className="h-8 w-56 pl-7 text-xs bg-zinc-950 border-zinc-700"
                  />
                </div>
                <input
                  type="date"
                  value={archivedDate}
                  onChange={(e) => setArchivedDate(e.target.value)}
                  data-testid="ff-archived-date"
                  className="h-8 px-2 text-xs rounded-md bg-zinc-950 border border-zinc-700 text-zinc-200"
                />
                {(archivedSearch || archivedDate) && (
                  <Button
                    variant="ghost" size="sm" onClick={() => { setArchivedSearch(""); setArchivedDate(""); }}
                    data-testid="ff-archived-clear" className="h-8 text-xs text-zinc-400 hover:text-zinc-200"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
            {archivedFares.length === 0 ? (
              <div className="text-xs text-zinc-500 italic py-6 text-center" data-testid="ff-archived-empty">
                No archived fares yet. Archive a completed fare to move it here.
              </div>
            ) : filteredArchived.length === 0 ? (
              <div className="text-xs text-zinc-500 italic py-6 text-center" data-testid="ff-archived-no-match">
                No archived fares match your search.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {filteredArchived.map((f) => (
                  <FareCard key={f.id} f={f} drivers={drivers} onAssign={assignDriver} assigning={assigning} onRelease={releaseFare} releasing={releasing} onCancel={cancelFare} cancelling={cancelling} onCloseOut={closeOutFare} closingOut={closingOut} onAutoRelease={setAutoRelease} autoReleasing={autoReleasing} archived onArchive={archiveFare} onUnarchive={unarchiveFare} archiving={archiving} />
                ))}
              </div>
            )}
          </Card>
        )}

        {/* Month calendar */}
        <Card className="bg-zinc-900 border-zinc-800 p-4">
          <div className="flex items-center justify-between mb-3">
            <button data-testid="ff-cal-prev" onClick={() => { setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1)); setSelectedDay(null); }}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-300"><ChevronLeft className="h-4 w-4" /></button>
            <div className="text-sm font-bold" data-testid="ff-cal-month">{MONTHS[viewMonth.getMonth()]} {viewMonth.getFullYear()}</div>
            <button data-testid="ff-cal-next" onClick={() => { setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1)); setSelectedDay(null); }}
              className="p-1.5 rounded hover:bg-zinc-800 text-zinc-300"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DOW.map((d) => <div key={d} className="text-center text-[10px] uppercase tracking-wider text-zinc-500 py-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calendarCells.map((cell, i) => {
              if (!cell) return <div key={`e${i}`} />;
              const hasReady = cell.fares.some((f) => isApproaching(f) && f.status !== "dispatched");
              const hasMissingDriver = cell.fares.some(isMissingDriver);
              const count = cell.fares.length;
              const isSel = isArchivedView ? archivedDate === cell.key : selectedDay === cell.key;
              const isToday = cell.key === todayKey;
              return (
                <button
                  key={cell.key}
                  data-testid={`ff-cal-day-${cell.key}`}
                  onClick={() => (isArchivedView ? setArchivedDate((prev) => (prev === cell.key ? "" : cell.key)) : setSelectedDay(cell.key))}
                  className={`min-h-[64px] rounded-md border p-1.5 text-left transition-colors relative
                    ${isSel ? "border-violet-400 bg-violet-500/10" : hasMissingDriver ? "border-rose-500/50 hover:border-rose-400 bg-rose-500/[0.04]" : "border-zinc-800 hover:border-zinc-600 bg-zinc-950/40"}
                    ${count > 0 ? "cursor-pointer" : "cursor-default"}`}
                >
                  {hasMissingDriver && (
                    <span className="absolute top-1 right-1" data-testid={`ff-cal-flag-${cell.key}`} title="Ride(s) without a driver">
                      <Flag className="h-3 w-3 text-rose-400 animate-pulse" />
                    </span>
                  )}
                  <div className={`text-[11px] font-bold ${isToday ? "text-violet-300" : "text-zinc-400"}`}>{cell.day}</div>
                  {count > 0 && (
                    <div className="mt-1 flex items-center gap-1">
                      <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold
                        ${hasMissingDriver ? "bg-rose-500/20 text-rose-300" : hasReady ? "bg-amber-400/20 text-amber-300" : "bg-violet-500/20 text-violet-300"}`}>
                        {hasReady && !hasMissingDriver && <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />}
                        {count} ride{count > 1 ? "s" : ""}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Day dispatch sheet */}
        {selectedDay && !isArchivedView && (
          <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="ff-day-sheet">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <h2 className="text-sm font-bold text-zinc-200">{fmtDateLong(selectedDay)}</h2>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="ff-day-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Filter this day…"
                  className="h-8 text-xs bg-zinc-950 border-zinc-700 w-48"
                />
                <span className="text-[11px] text-zinc-500">{filteredSelectedDay.length} ride(s)</span>
              </div>
            </div>
            {filteredSelectedDay.length === 0 ? (
              <div className="text-xs text-zinc-500 italic py-6 text-center">No future fares on this day.</div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {filteredSelectedDay.map((f) => (
                  <FareCard key={f.id} f={f} drivers={drivers} onAssign={assignDriver} assigning={assigning} onRelease={releaseFare} releasing={releasing} onCancel={cancelFare} cancelling={cancelling} onCloseOut={closeOutFare} closingOut={closingOut} onAutoRelease={setAutoRelease} autoReleasing={autoReleasing} archived={isArchivedView} onArchive={archiveFare} onUnarchive={unarchiveFare} archiving={archiving} />
                ))}
              </div>
            )}
          </Card>
        )}

        {!loading && !isArchivedView && activeFares.length === 0 && (
          <div className="text-center py-12 text-zinc-500 text-sm italic" data-testid="ff-empty">
            No upcoming future fares yet. Advance bookings (24h+) will appear here.
          </div>
        )}
      </div>

      <ImportScheduledDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
    </div>
  );
}
